import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Link, Loading } from "@carbon/react";
import AuthLayout from "../../components/carbon/AuthLayout/AuthLayout";
import { WHATSAPP } from "../../utils";
import { useUserValidate } from "../../hooks/useUserValidate";
import "./validateUser.scss";

/**
 * The email-validation screen (`GET /auth/validate-email/{token}/{email}`, token + email read
 * from the URL query), rebuilt on the Carbon primitives (Task 6). Task 2 made the endpoint
 * respond 204 No Content instead of the raw `User` — this screen never read the response body
 * before (only `isPending`/`isSuccess`/`isError` from the mutation), so that's unaffected.
 *
 * The three render states (click-to-verify / success / error) are carried over verbatim,
 * INCLUDING the pre-existing `t("register.apologies")` bug the e2e safety net pins: the real
 * locale key is `register.appologies` (double p, see src/locales/{en,es,pt}.json), so this call
 * has never resolved to real copy — i18next falls back to printing the raw key
 * "register.apologies" on screen. Not fixed here; reproduced as-is so the pinned e2e assertion
 * (`page.getByText("register.apologies")`) stays meaningful as a "don't silently change this"
 * marker rather than this task quietly patching a bug the brief didn't ask it to touch.
 */
export default function ValidateUser() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const {
    mutate: validateUser,
    isPending,
    isSuccess,
    isError,
  } = useUserValidate();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  let message = (
    <>
      <p className="validate-user__copy">{t("register.clickVerify")}</p>
      <Button
        className="validate-user__cta"
        onClick={() => validateUser({ token: token || "", email: email || "" })}
      >
        {t("register.verify")}
      </Button>
    </>
  );

  if (isSuccess) {
    message = (
      <div className="validate-user__result">
        <p className="validate-user__copy validate-user__copy--center">
          <span className="validate-user__status validate-user__status--success">
            {t("register.accountValidated")}
          </span>
          <br />
          <br />
          {t("register.briefly")}
          <br />
          <br />
          {t("register.checkSpam")}
          <br /> {t("register.thanks")}
        </p>
        <Link href="/" className="validate-user__cta">
          {t("register.login")}
        </Link>
      </div>
    );
  }

  if (isError) {
    message = (
      <div className="validate-user__result">
        <p className="validate-user__copy validate-user__copy--center">
          <span className="validate-user__status validate-user__status--error">
            {t("register.error")}
          </span>
          <br />
          <br />
          {t("register.contact")}{" "}
          <Link href={WHATSAPP} target="_blank" rel="noreferrer">
            {t("register.support")}
          </Link>{" "}
          {t("register.help")}
          <br />
          <br /> {t("register.apologies")}
          <br />
          <br />
          {/* The link works once: opened again after validating, it lands here. */}
          {t("register.alreadyValidated")}{" "}
          <Link href="/">{t("register.login")}</Link>
        </p>
        <Link href="https://simbuy.io" className="validate-user__cta">
          {t("register.visit")}
        </Link>
      </div>
    );
  }

  return (
    <AuthLayout>
      {isPending ? (
        <Loading withOverlay={false} description={t("cta.loading")} />
      ) : (
        message
      )}
    </AuthLayout>
  );
}
